import React, { createContext, useContext, useEffect, useState } from 'react';
import { apiService } from '@/services/api';
import { captureTenantKey, safeHttpsUrl, safePrimaryColor, setTenantDashboardUrl } from '@/lib/tenant';
import { DASHBOARD_LOGIN_URL } from '@/lib/utils';
import { setServedStates } from '@/data/conciergeCommon';

// §tenant-branded intake §2: on boot, if a tenant key is present, fetch that
// tenant's branding (GET /tenant/config?t=…) and apply it — logo (via <BrandLogo>),
// primary color (runtime stylesheet), document title + favicon, support email.
// No key → default Fitpal look, no network call.
//
// The app's canonical Fitpal green (#2d8a5f, hardcoded ~231×) is preserved for
// tenant 1: we only apply the COLOR override for real tenants (slug !== 'fitpal')
// so an accidental ?t=<fitpal-key> never re-themes Fitpal. Logo/title/favicon
// apply from whatever config returns.

const TenantBrandingContext = createContext({
  logoUrl: null, displayName: null, supportEmail: null, tenantSlug: null, ready: false,
});

export const useTenantBranding = () => useContext(TenantBrandingContext);

// Darken a #rrggbb by `amt` (0–1) for hover/active shades.
function darken(hex, amt = 0.15) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const f = (v) => Math.max(0, Math.round(v * (1 - amt)));
  const r = f((n >> 16) & 255), g = f((n >> 8) & 255), b = f(n & 255);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

// Inject a stylesheet that remaps the app's hardcoded brand-green Tailwind
// arbitrary-value classes to the tenant color. Enumerated from actual usage
// (text/bg/border/hover/group-hover/ring/from + the soft-panel tint).
// `tenantAccent` (a tenant other than Fitpal) also feeds the intake's warm
// theme (components/preview/preview.css reads --wl-tenant-accent), so a branded
// link renders in the tenant's color while Fitpal keeps its own accent.
function applyBrandColor(primary, tenantAccent = false) {
  const dark = darken(primary, 0.15);
  const soft = `color-mix(in srgb, ${primary} 8%, white)`;
  const P = '2d8a5f', D = '236b4a', S = 'f0faf4';
  const accent = tenantAccent ? `--wl-tenant-accent:${primary};--wl-tenant-accent-dark:${dark};` : '';
  const css = `
:root{--brand-primary:${primary};--brand-primary-dark:${dark};${accent}}
.text-\\[\\#${P}\\]{color:var(--brand-primary)!important}
.bg-\\[\\#${P}\\]{background-color:var(--brand-primary)!important}
.border-\\[\\#${P}\\],.border-\\[\\#${P}\\]\\/15,.border-\\[\\#${P}\\]\\/20,.border-\\[\\#${P}\\]\\/30{border-color:var(--brand-primary)!important}
.hover\\:text-\\[\\#${P}\\]:hover{color:var(--brand-primary)!important}
.hover\\:border-\\[\\#${P}\\]:hover{border-color:var(--brand-primary)!important}
.group:hover .group-hover\\:text-\\[\\#${P}\\]{color:var(--brand-primary)!important}
.group:hover .group-hover\\:border-\\[\\#${P}\\]{border-color:var(--brand-primary)!important}
.focus\\:ring-\\[\\#${P}\\]:focus,.focus\\:ring-\\[\\#${P}\\]\\/50:focus,.focus-visible\\:ring-\\[\\#${P}\\]:focus-visible,.focus-visible\\:ring-\\[\\#${P}\\]\\/40:focus-visible{--tw-ring-color:var(--brand-primary)!important}
.from-\\[\\#${P}\\]{--tw-gradient-from:var(--brand-primary) var(--tw-gradient-from-position)!important;--tw-gradient-stops:var(--tw-gradient-from),var(--tw-gradient-to)!important}
.hover\\:text-\\[\\#${D}\\]:hover{color:var(--brand-primary-dark)!important}
.bg-\\[\\#${S}\\]{background-color:${soft}!important}
.from-\\[\\#${S}\\]{--tw-gradient-from:${soft} var(--tw-gradient-from-position)!important;--tw-gradient-stops:var(--tw-gradient-from),var(--tw-gradient-to)!important}
`;
  let el = document.getElementById('tenant-brand-style');
  if (!el) { el = document.createElement('style'); el.id = 'tenant-brand-style'; document.head.appendChild(el); }
  el.textContent = css;
}

function setFavicon(url) {
  if (!url) return;
  document.querySelectorAll('link[rel~="icon"], link[rel="shortcut icon"]').forEach((l) => { l.href = url; });
}

export function TenantBrandingProvider({ children }) {
  const [state, setState] = useState({ logoUrl: null, displayName: null, supportEmail: null, tenantSlug: null, dashboardUrl: DASHBOARD_LOGIN_URL, ready: false });

  useEffect(() => {
    const key = captureTenantKey();
    if (!key) { setState((s) => ({ ...s, dashboardUrl: DASHBOARD_LOGIN_URL, ready: true })); return undefined; }
    let alive = true;
    apiService.getTenantConfig(key)
      .then((cfg) => {
        if (!alive || !cfg) return;
        const b = cfg.branding || {};
        // Apply whatever the resolved tenant configured — no slug gate. A `?t=`
        // key is an explicit branded entry point, so the tenant's own color
        // applies even when its slug is 'fitpal' (e.g. "Fitpal V2"). primaryColor
        // is validated to strict #rrggbb before it hits the injected stylesheet.
        if (b.primaryColor) applyBrandColor(safePrimaryColor(b.primaryColor), cfg.slug !== 'fitpal');
        if (cfg.displayName) document.title = cfg.displayName;
        const favicon = safeHttpsUrl(b.faviconUrl);
        if (favicon) setFavicon(favicon);
        // Tenant-scoped dashboard/login URL (patients complete follow-ups + sign
        // in there). BE-owned (from custom_domain/subdomain); falls back to the
        // Fitpal default until the BE returns dashboardUrl. Persist for the
        // imperative redirect in CompleteStep.
        const dashboardUrl = safeHttpsUrl(cfg.dashboardUrl) || DASHBOARD_LOGIN_URL;
        setTenantDashboardUrl(dashboardUrl);
        // Concierge programs' served states are BE-owned (present only while
        // ADVANCED_PEPTIDE_ENABLED); the intake's constant is the fallback.
        const served = (cfg.conciergePrograms && cfg.conciergePrograms.servedStates)
          || (cfg.advancedPeptide && cfg.advancedPeptide.servedStates);
        if (served) setServedStates(served);
        setState({
          logoUrl: safeHttpsUrl(b.logoUrl),
          displayName: cfg.displayName || null,
          supportEmail: b.supportEmail || null,
          tenantSlug: cfg.slug || null,
          dashboardUrl,
          ready: true,
        });
      })
      .catch(() => { if (alive) setState((s) => ({ ...s, ready: true })); });
    return () => { alive = false; };
  }, []);

  return <TenantBrandingContext.Provider value={state}>{children}</TenantBrandingContext.Provider>;
}
